/**
 * LoadingSpinner Component - Foundational Primitive #14 (Loading)
 *
 * Loading spinner with optional text. Uses CSS primitive classes from:
 * - 14-loading-tooltip-toggle-link.css for .loading-spinner styling
 *
 * Features:
 * - Maroon spinner color (per DO THIS/NOT THAT guidelines)
 * - Multiple sizes (sm, md, lg)
 * - Full-screen mode support
 * - Accessible with ARIA labels
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../hooks/useThemeClasses';
import '../../theme-components/14-loading-tooltip-toggle-link.css';

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  className?: string;
  fullScreen?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  text,
  className = '',
  fullScreen = false
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  // Use translation for default loading text
  const defaultText = t('forms.loading');

  // Size configurations
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  // Get primary color with fallback
  const primaryColor = theme?.brand?.colors?.primary; // fallback to blue-600

  const spinnerContent = (
    <div className="text-center">
      <div
        className={cn(
          'loading-spinner',  // CSS Primitive from 14-loading-tooltip-toggle-link.css
          'animate-spin rounded-full h-8 w-8 border-2 border-t-transparent mx-auto mb-4'
        )}
        style={{ borderColor: primaryColor, borderTopColor: 'transparent' }}
      />
      {text && (
        <p className="text-gray-600">
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center bg-gray-50', className)}>
        {spinnerContent}
      </div>
    );
  }

  return (
    <div className={cn('flex items-center justify-center', className)}>
      {spinnerContent}
    </div>
  );
};

export default LoadingSpinner;